import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import Button from '../Button/Button';
import Banner from '../Banner/Banner';
import SectionPliable from '../SectionPliable/SectionPliable';
import SuggererLotModal from './SuggererLotModal';
import './EstimationBesoins.css';

interface LotTechnique {
  id: number;
  code: string;
  nom?: string;
  description?: string;
  actif?: boolean;
}

interface PiedAnnee {
  total_financier: number;
  nombre: number;
  bc: number;
  ms: number;
  montant_moyen: number;
}

export interface DerniereMaj {
  date: string | null;
  par: string | null;
}

// Reponse de GET /estimation-besoins.
interface EstimationData {
  annees: number[];
  lots: Record<string, Record<string, number>>;       // passe calcule (commandes)
  pied: Record<string, PiedAnnee>;
  previsions: Record<string, Record<string, number>>; // previsions saisies (annee en cours + futures)
  derniere_maj: DerniereMaj | null;
  sources: Record<string, boolean>;
}

interface EstimationBesoinsProps {
  // Remontee de la derniere modification (date + auteur) pour la banniere "Derniere mise a jour".
  onMajChange?: (maj: DerniereMaj | null) => void;
}

// Nombre d'annees futures affichees apres l'annee en cours.
const FUTURE_HORIZON = 3;

/**
 * Section "Statistiques et estimation financiere des besoins" (onglet acheteur).
 * - annees passees : consommation reelle de l'organisme (commandes archivees
 *   bdc_archive, statut "accepté"), lecture seule. TOUTES les annees archivees sont
 *   chargees ; l'affichage est centre sur l'annee en cours (5 colonnes visibles).
 * - annee en cours + suivantes : previsions saisies par l'acheteur, PERSISTEES,
 *   prechargees par defaut avec la conso passee de l'organisme (estimation grisee).
 * Colonnes Lot + Libelle figees (sticky), annees en scroll horizontal (fleches < >).
 */
const EstimationBesoins: React.FC<EstimationBesoinsProps> = ({ onMajChange }) => {
  const [lots, setLots] = useState<LotTechnique[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // Chargement de la conso passee (bdc_archive) : tant qu'il court, les annees a
  // afficher ne sont pas connues -> on garde le skeleton (pas de slide de centrage).
  const [passeLoading, setPasseLoading] = useState<boolean>(true);
  const [previsions, setPrevisions] = useState<Record<string, Record<number, string>>>({});
  const [passe, setPasse] = useState<EstimationData | null>(null);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  // Feedback de sauvegarde : etat global + cellules a flasher en vert apres save.
  const [saveStatus, setSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved'>('idle');
  const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());
  // Modale de suggestion d'un nouveau lot technique (bouton "+ Autres").
  const [suggestOpen, setSuggestOpen] = useState<boolean>(false);
  // Infobulle (instantanee) du nom de lot quand il est tronque par la colonne bornee.
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const savedRef = useRef<Record<string, Record<number, string>>>({});

  // Conteneur scrollable + une cellule d'annee pour mesurer la largeur d'un pas de scroll.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const yearColRef = useRef<HTMLTableCellElement | null>(null);
  // Mesure de la largeur reelle de la colonne Lot pour positionner le sticky de Libelle.
  const tableRef = useRef<HTMLTableElement | null>(null);
  const lotThRef = useRef<HTMLTableCellElement | null>(null);
  const libelleThRef = useRef<HTMLTableCellElement | null>(null);
  // Largeur d'une colonne d'annee (entier px) : pose par recompute, lue par le centrage
  // et les fleches. Entier -> colonnes strictement egales -> (idx-2)*yw tombe pile.
  const ywRef = useRef(0);
  // Autosave debounce (3s d'inactivite) : cellules en attente + timer.
  const pendingSaves = useRef<Map<string, { lot: LotTechnique; year: number; value: string }>>(new Map());
  const saveTimer = useRef<number | null>(null);

  const currentYear = new Date().getFullYear();

  // Annees affichees : de la PLUS ANCIENNE annee passee ayant des donnees (bdc_archive)
  // jusqu'a l'annee en cours + 3 futures. Aucune borne basse fixe : si l'organisme a
  // 5 ans d'archive, on charge les 5 ; s'il n'a que n-1, on n'affiche que n-1 + courante
  // + futures. Les annees intermediaires sans donnees affichent "N/A". L'affichage est
  // centre sur l'annee en cours (les anterieures restent atteignables via les fleches).
  const years = useMemo(() => {
    const pasts = (passe?.annees ?? []).filter((y) => y < currentYear);
    let earliest: number;
    if (pasts.length) {
      earliest = Math.min(...pasts);
    } else if (passeLoading) {
      // Le temps que la conso passee charge, le skeleton affiche des annees passees
      // par defaut (n-3) pour ne pas apparaitre tronque a gauche.
      earliest = currentYear - 3;
    } else {
      earliest = currentYear;
    }
    const arr: number[] = [];
    for (let y = earliest; y <= currentYear + FUTURE_HORIZON; y++) arr.push(y);
    return arr;
  }, [passe, passeLoading, currentYear]);

  // Miroirs (refs) lus par recompute/centerScroll/setWrap sans les rendre dependants de
  // ces valeurs (les callbacks restent stables, donc le conteneur n'est pas re-cree).
  const yearsRef = useRef(years);
  yearsRef.current = years;
  const lotsRef = useRef(lots);
  lotsRef.current = lots;
  // Donnees pretes = lots ET conso passee chargees : condition pour reveler le tableau.
  const dataSettled = lots !== null && !loading && !passeLoading;
  const dataSettledRef = useRef(dataSettled);
  dataSettledRef.current = dataSettled;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getLotsTechniques()
      .then((data: LotTechnique[]) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? [...data] : [];
        list.sort((a, b) => Number(a.code) - Number(b.code));
        setLots(list);
      })
      .catch(() => {
        if (!cancelled) setLots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    apiService.getEstimationBesoins()
      .then((data: EstimationData | null) => {
        if (cancelled || !data) return;
        setPasse(data);
        onMajChange?.(data.derniere_maj ?? null);
        const init: Record<string, Record<number, string>> = {};
        Object.entries(data.previsions || {}).forEach(([code, byYear]) => {
          init[code] = {};
          Object.entries(byYear).forEach(([annee, montant]) => {
            // Stockage interne en virgule (fr) : la saisie et l'affichage gardent les decimales.
            init[code][Number(annee)] = String(montant).replace('.', ',');
          });
        });
        setPrevisions(init);
        savedRef.current = JSON.parse(JSON.stringify(init));
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setPasseLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  // Position des fleches flottantes : zone des annees (apres les colonnes figees), a hauteur
  // de l'entete, chaque fleche centree sur la 1ere / derniere colonne d'annee visible.
  const [arrowBox, setArrowBox] = useState<{ left: number; right: number; height: number; pad: number }>({ left: 0, right: 0, height: 0, pad: 0 });
  // Skeleton tant que la mise en page n'est pas calculee (au chargement et a chaque
  // expand de la section) : evite le "saut" du tableau. Remis a false a chaque
  // montage du conteneur (setWrap), repasse a true une frame apres le recompute.
  const [ready, setReady] = useState<boolean>(false);

  // table-layout: auto -> N° et Lot technique prennent la place necessaire (fit
  // content). On mesure leurs largeurs reelles puis on impose aux colonnes d'annees
  // une largeur egale = (largeur visible - N° - Lot) / 5, pour qu'EXACTEMENT 5 annees
  // remplissent toute la largeur ; le tableau reste plus large (7 annees) -> les 2
  // extremes sont atteignables via les fleches.
  const recompute = useCallback((): number => {
    const wrap = scrollRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return 0;
    // Le basculement auto->fixed ci-dessous peut clamper le scroll : on le memorise pour
    // le restaurer a la fin (ne pas casser un centrage ou une position en cours).
    const savedScroll = wrap.scrollLeft;
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    const nbYears = yearsRef.current.length || 1;

    // 1) Passe de MESURE : layout auto + largeurs retirees -> N° et Lot prennent leur
    //    largeur naturelle (fit content), qu'on lit avant de figer.
    table.style.tableLayout = 'auto';
    table.style.width = 'auto';
    table.style.removeProperty('--lot-w');
    table.style.removeProperty('--libelle-w');
    table.style.removeProperty('--year-col-w');
    const lotW = lotThRef.current?.offsetWidth ?? 0;
    // Colonne Lot bornee (max 24rem) : un nom tres long ne reduit pas les colonnes
    // d'annees ; tronquee a l'ellipsis, le nom complet reste lisible via l'infobulle
    // (title) sur la cellule.
    const libW = Math.min(libelleThRef.current?.offsetWidth ?? 0, Math.round(24 * remPx));

    // 2) Largeur d'annee = ENTIER (pas de sous-pixel) -> colonnes strictement egales,
    //    et (idx-2)*yw tombe pile sur une frontiere de colonne. visibleCols (<=5)
    //    colonnes doivent remplir la zone visible ; le RESTE de division est absorbe par
    //    la colonne Lot (libAdj) -> remplissage EXACT (n-2..n+2 pile, pas de peek a droite).
    const visibleCols = Math.min(5, nbYears);
    const avail = wrap.clientWidth - lotW - libW;
    const yw = Math.max(Math.round(8 * remPx), Math.floor(avail / visibleCols));
    const reste = avail - visibleCols * yw;
    const libAdj = reste > 0 ? libW + reste : libW;

    // 3) Application en layout FIXE : chaque colonne fait EXACTEMENT sa largeur (entiere)
    //    -> colonnes d'annees toutes egales, centrage qui tombe pile.
    table.style.setProperty('--lot-w', `${lotW}px`);
    table.style.setProperty('--libelle-w', `${libAdj}px`);
    table.style.setProperty('--year-col-w', `${yw}px`);
    table.style.tableLayout = 'fixed';
    table.style.width = `${lotW + libAdj + nbYears * yw}px`;

    ywRef.current = yw;
    // Fleches : sur la zone des annees (left = largeur des colonnes figees), vers les EXTREMITES
    // (bord gauche 1ere annee / bord droit derniere) avec un petit retrait du bord, a hauteur entete.
    setArrowBox({
      left: lotW + libAdj,
      right: Math.max(0, wrap.offsetWidth - wrap.clientWidth),
      height: yearColRef.current?.offsetHeight ?? 0,
      pad: Math.round(0.6 * remPx),
    });
    wrap.scrollLeft = savedScroll; // restaure la position (clampee au nouveau maxScroll)
    setCanScrollLeft(wrap.scrollLeft > 1);
    setCanScrollRight(wrap.scrollLeft < wrap.scrollWidth - wrap.clientWidth - 1);
    return yw;
  }, []);

  // Centre l'affichage sur l'annee en cours : on amene la colonne n-2 au bord gauche de
  // la zone d'annees -> scrollLeft = (idx_courante - 2) * yw. Les colonnes d'annees etant
  // des ENTIERS strictement egaux (cf. recompute), ce produit tombe pile sur une frontiere
  // -> n-2..n+2 remplissent exactement la zone visible. Moins de 2 annees passees -> 0.
  // scrollTo avec behavior explicite ('smooth' anime, 'auto' instantane), appele APRES
  // recompute (layout stable) -> fiable, sans dependre du scroll-behavior CSS.
  const centerScroll = useCallback((node: HTMLDivElement, smooth: boolean) => {
    const yw = ywRef.current;
    const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
    const idx = yearsRef.current.indexOf(currentYear);
    const dest = Math.min(Math.max(0, idx - 2) * yw, maxScroll);
    node.scrollTo({ left: dest, behavior: smooth ? 'smooth' : 'auto' });
    setCanScrollLeft(dest > 1);
    setCanScrollRight(dest < maxScroll - 1);
  }, [currentYear]);

  // Callback ref du conteneur scrollable : (re)branche scroll + ResizeObserver et
  // RECALCULE a chaque montage. Cle pour le collapse/expand de la section pliable :
  // le conteneur est demonte/remonte, donc on recalcule (sinon le tableau retombe
  // en fallback et affiche toutes les colonnes au lieu de 5).
  const wrapCleanup = useRef<(() => void) | null>(null);
  const setWrap = useCallback((node: HTMLDivElement | null) => {
    wrapCleanup.current?.();
    wrapCleanup.current = null;
    scrollRef.current = node;
    if (!node) { setReady(false); return; }
    // Montage (initial ou expand) : on repart en skeleton (au bord gauche -> annees
    // passees visibles) et on calcule la mise en page.
    setReady(false);
    const onScroll = () => {
      setCanScrollLeft(node.scrollLeft > 1);
      setCanScrollRight(node.scrollLeft < node.scrollWidth - node.clientWidth - 1);
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    // IMPORTANT : ne reagir qu'aux changements de LARGEUR. Au reveal (skeleton -> lignes
    // reelles) la HAUTEUR du tableau change ; sans ce garde, le RO relancerait recompute()
    // (donc le basculement auto->fixed) EN PLEINE animation de centrage -> scroll casse.
    let lastW = node.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = node.clientWidth;
      if (w === lastW) return;
      lastW = w;
      recompute();
      // Un vrai changement de LARGEUR (resize fenetre) -> on recadre pile sur l'annee en cours.
      centerScroll(node, false);
    });
    ro.observe(node);
    recompute();
    // Si les donnees sont deja pretes (cas expand), on revele et on centre EN DOUCEUR a
    // la frame suivante (layout stable). Au 1er chargement (lots/conso pas encore la),
    // l'effet ci-dessous s'en charge une fois les donnees arrivees.
    let raf = 0;
    if (dataSettledRef.current) {
      raf = requestAnimationFrame(() => { centerScroll(node, true); setReady(true); });
    }
    wrapCleanup.current = () => {
      node.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [recompute, centerScroll]);

  // Quand le contenu final arrive (lots + conso passee bdc_archive, donc les annees a
  // afficher), on recalcule la mise en page, on RECENTRE (instantanement) sur l'annee
  // en cours, puis on revele. Tant que tout n'est pas pret on reste en skeleton -> le
  // tableau n'apparait qu'une fois deja centre (jamais de slide visible).
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    recompute();
    if (dataSettled) {
      // Donnees pretes : on centre EN DOUCEUR sur l'annee en cours (apres recompute,
      // layout stable) puis on revele le tableau.
      const raf = requestAnimationFrame(() => { centerScroll(node, true); setReady(true); });
      return () => cancelAnimationFrame(raf);
    }
  }, [lots, loading, passeLoading, passe, dataSettled, recompute, centerScroll]);

  const scrollYears = (dir: 1 | -1) => {
    const wrap = scrollRef.current;
    if (!wrap) return;
    const col = ywRef.current || yearColRef.current?.offsetWidth || 100;
    wrap.scrollBy({ left: dir * col, behavior: 'smooth' });
  };

  const isEditable = (year: number) => year >= currentYear;
  const keyOf = (code: string, year: number) => `${code}:${year}`;

  // Affichage : 2 decimales (chiffres a virgule), separateurs fr (espace milliers, virgule decimale).
  const fmt2 = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtEuro = (n: number) => `${fmt2(n)} €`;
  const fmtNumber = (n: number) => fmt2(n);
  const fmtVariation = (cur: number, prev: number) => {
    if (!prev) return '';
    const pct = Math.round(((cur - prev) / prev) * 100);
    return ` (${pct > 0 ? '+' : ''}${pct}%)`;
  };

  // Estimation par defaut d'une cellule editable non saisie : moyenne de la conso passee.
  const estimationDefault = (code: string): number => {
    // Moyenne sur TOUTES les annees passees calculees (pas seulement celles affichees).
    const pastAnnees = passe?.annees ?? [];
    if (!pastAnnees.length) return 0;
    const sum = pastAnnees.reduce((s, y) => s + (passe?.lots?.[code]?.[String(y)] ?? 0), 0);
    return sum / pastAnnees.length;
  };

  // Valeur "brute" affichee quand la cellule a le focus (saisie en cours, virgule autorisee).
  const rawValue = (code: string, year: number): string => {
    const saisi = previsions[code]?.[year];
    if (saisi !== undefined) return saisi;
    const est = estimationDefault(code);
    return est > 0 ? String(Math.round(est * 100) / 100).replace('.', ',') : '';
  };

  // Valeur numerique effective (parse la virgule eventuelle). PAS d'arrondi a l'euro.
  const effectiveValue = (code: string, year: number): number => {
    const saisi = previsions[code]?.[year];
    if (saisi !== undefined) return parseFloat((saisi || '0').replace(',', '.')) || 0;
    return estimationDefault(code);
  };

  // Persiste les cellules en attente (uniquement celles qui ont change depuis la derniere sauvegarde).
  const flushSaves = () => {
    const pending = pendingSaves.current;
    pendingSaves.current = new Map();
    const toSave: { lot: LotTechnique; year: number; value: string }[] = [];
    pending.forEach((e) => {
      const prevSaved = savedRef.current[e.lot.code]?.[e.year] ?? '';
      if (e.value !== prevSaved) toSave.push(e);
    });
    if (!toSave.length) {
      setSaveStatus('idle');
      return;
    }
    setSaveStatus('saving');
    Promise.all(
      toSave.map(({ lot, year, value }) => {
        const montant = parseFloat((value || '0').replace(',', '.')) || 0;
        return apiService.saveEstimationPrevision(lot.id, year, montant).then((data: { derniere_maj?: DerniereMaj | null } | null) => {
          savedRef.current[lot.code] = { ...(savedRef.current[lot.code] || {}), [year]: value };
          if (data && 'derniere_maj' in data) onMajChange?.(data.derniere_maj ?? null);
          return keyOf(lot.code, year);
        });
      }),
    )
      .then((keys) => {
        setSaveStatus('saved');
        setFlashKeys(new Set(keys)); // flash vert sur les cellules sauvegardees
        window.setTimeout(() => setFlashKeys(new Set()), 1400);
        window.setTimeout(() => setSaveStatus('idle'), 2500);
      })
      .catch(() => setSaveStatus('idle'));
  };

  // Saisie d'une cellule : maj de l'etat + autosave apres 3s sans nouvelle activite.
  const handleChange = (lot: LotTechnique, year: number, value: string) => {
    // Chiffres + UNE seule virgule decimale (le point saisi est converti en virgule).
    let cleaned = value.replace(/[^\d.,]/g, '').replace(/\./g, ',');
    const i = cleaned.indexOf(',');
    if (i !== -1) {
      cleaned = cleaned.slice(0, i + 1) + cleaned.slice(i + 1).replace(/,/g, '');
    }
    setPrevisions((prev) => ({ ...prev, [lot.code]: { ...(prev[lot.code] || {}), [year]: cleaned } }));
    setSaveStatus('pending');
    pendingSaves.current.set(`${lot.code}:${year}`, { lot, year, value: cleaned });
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(flushSaves, 3000);
  };

  // Flush des sauvegardes en attente au demontage (ex. changement d'onglet avant les 3s).
  const flushRef = useRef(flushSaves);
  flushRef.current = flushSaves;
  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    flushRef.current();
  }, []);

  // Rafraichit la derniere maj depuis le serveur (ex. apres une suggestion via la modale).
  const refetchMaj = () => {
    apiService.getEstimationBesoins()
      .then((d: EstimationData | null) => onMajChange?.(d?.derniere_maj ?? null))
      .catch(() => undefined);
  };

  // Montant passe d'un lot pour une annee : 0 € si aucune commande archivee (pas de N/A).
  const passeMontant = (code: string, year: number): string =>
    fmtEuro(passe?.lots?.[code]?.[String(year)] ?? 0);
  const piedAnnee = (year: number): PiedAnnee | undefined => passe?.pied?.[String(year)];

  const totalForYear = (year: number) => {
    if (isEditable(year)) {
      // Total previsionnel = UNIQUEMENT les montants reellement saisis (les suggestions grisees
      // issues de la conso passee restent affichees dans les cellules mais ne comptent pas).
      // Doit coincider avec la carte Budget (somme des previsions persistees cote backend).
      return (lots || []).reduce((sum, l) => {
        const saisi = previsions[l.code]?.[year];
        return sum + (saisi !== undefined ? (parseFloat((saisi || '0').replace(',', '.')) || 0) : 0);
      }, 0);
    }
    return piedAnnee(year)?.total_financier ?? 0;
  };

  const piedCompteur = (year: number, key: 'nombre' | 'bc' | 'ms'): number =>
    isEditable(year) ? 0 : (piedAnnee(year)?.[key] ?? 0);


  // Skeleton tant que le tableau n'est pas "pret" : ready ne passe a true qu'une fois
  // les donnees chargees (lots + conso passee) ET la mise en page calculee + centree.
  // -> le tableau reel n'apparait qu'a sa position finale (aucun saut, aucun slide).
  // Nombre de lignes = nb de lots si connus (hauteur identique au reel), sinon defaut.
  const showSkeleton = !ready;
  const skeletonRowCount = (lots && lots.length > 0) ? lots.length : 8;

  return (
    <>
      <SectionPliable
        className="estimation-besoins-block"
        title={(
          <>
            Statistiques et évaluation des besoins
            <span className="estimation-besoins-asterisk">*</span>
          </>
        )}
        headerRight={(
          <span className={`estimation-besoins-savestatus${saveStatus === 'saved' ? ' is-saved' : ''}`}>
            {saveStatus === 'pending' && 'Modification en attente…'}
            {saveStatus === 'saving' && 'Enregistrement…'}
            {saveStatus === 'saved' && '✓ Enregistré'}
          </span>
        )}
      >
        <Banner
          variant="info"
          className="estimation-besoins-notice"
          message="Le formulaire déclaratif relatif aux besoins est non contractuel et ne constitue aucun engagement. Il est préchargé par défaut avec les statistiques moyennes de Centralis."
        />
        <div className="estimation-besoins-table-zone">
        <div
          className="estimation-besoins-carousel-bar"
          style={{ left: arrowBox.left, right: arrowBox.right, height: arrowBox.height || undefined, paddingLeft: arrowBox.pad, paddingRight: arrowBox.pad }}
        >
          <button
            type="button"
            className="estimation-besoins-arrow"
            onClick={() => scrollYears(-1)}
            disabled={!canScrollLeft}
            aria-label="Années précédentes"
          >‹</button>
          <button
            type="button"
            className="estimation-besoins-arrow"
            onClick={() => scrollYears(1)}
            disabled={!canScrollRight}
            aria-label="Années suivantes"
          >›</button>
        </div>
        <div className="estimation-besoins-table-wrap" ref={setWrap}>
          <table className="estimation-besoins-table" ref={tableRef}>
            <colgroup>
              <col className="cg-lot" />
              <col className="cg-libelle" />
              {years.map((y) => <col key={y} className="cg-annee" />)}
            </colgroup>
            <thead>
              <tr>
                <th className="col-lot" ref={lotThRef}>N°</th>
                <th className="col-libelle" ref={libelleThRef}>Lot technique</th>
                {years.map((y, idx) => (
                  <th
                    key={y}
                    ref={(el) => {
                      if (idx === 0) yearColRef.current = el;
                    }}
                    className={isEditable(y) ? 'col-annee' : 'col-annee is-past'}
                  >
                    {y}
                    {isEditable(y) && <span className="estimation-besoins-previsionnel">(prévisionnel)</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showSkeleton
                ? Array.from({ length: skeletonRowCount }).map((_, i) => (
                    <tr key={`sk${i}`} className="estim-skel-row">
                      <td className="col-lot"><span className="estim-skel-bar estim-skel-bar--code" /></td>
                      <td className="col-libelle"><span className="estim-skel-bar estim-skel-bar--lot" /></td>
                      {years.map((y) => <td key={y} className="col-annee"><span className="estim-skel-bar" /></td>)}
                    </tr>
                  ))
                : (lots || []).map((lot) => (
                <tr key={lot.id}>
                  <td className="col-lot">{lot.code}</td>
                  <td
                    className="col-libelle"
                    onMouseEnter={(e) => {
                      // Infobulle sur toute cellule Lot non vide (valeur entiere au survol).
                      const text = lot.description || lot.nom || '';
                      if (!text) return;
                      const r = e.currentTarget.getBoundingClientRect();
                      setTip({ x: r.left, y: r.bottom + 4, text });
                    }}
                    onMouseLeave={() => setTip(null)}
                  >
                    <span className="col-libelle-text">{lot.description || lot.nom}</span>
                  </td>
                  {years.map((y) => {
                    const editable = isEditable(y);
                    const focused = focusedKey === keyOf(lot.code, y);
                    const saisi = previsions[lot.code]?.[y] !== undefined;
                    return (
                      <td key={y} className={editable ? 'col-annee is-editable' : 'col-annee is-past'}>
                        {editable ? (
                          <span className="estimation-besoins-input-wrap">
                            <input
                              type="text"
                              inputMode="decimal"
                              className={`estimation-besoins-input${saisi ? '' : ' is-estimation'}${flashKeys.has(keyOf(lot.code, y)) ? ' is-saved' : ''}`}
                              value={focused ? rawValue(lot.code, y) : fmtNumber(effectiveValue(lot.code, y))}
                              onChange={(e) => handleChange(lot, y, e.target.value)}
                              onFocus={(e) => { setFocusedKey(keyOf(lot.code, y)); const el = e.currentTarget; requestAnimationFrame(() => { if (document.activeElement === el) el.select(); }); }}
                              onBlur={() => setFocusedKey(null)}
                              placeholder="0"
                              aria-label={`${lot.code} ${y}`}
                            />
                            <span className="estimation-besoins-euro">€</span>
                          </span>
                        ) : (
                          <span className="estimation-besoins-readonly">{passeMontant(lot.code, y)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Nouveau / Autres : suggerer un nouveau lot (cachee pendant le skeleton). */}
              {!showSkeleton && (
                <tr className="estimation-besoins-nouveau-row">
                  <td className="col-lot" />
                  <td className="col-libelle">
                    <Button variant="secondary" className="estimation-besoins-suggest-btn" icon="+" value="Suggérer un lot" onClick={() => setSuggestOpen(true)} />
                  </td>
                  {/* Cellules par annee (et non un colSpan) pour garder les bordures verticales. */}
                  {years.map((y) => <td key={y} className="col-annee" />)}
                </tr>
              )}
            </tbody>
            <tfoot>
              {showSkeleton ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skf${i}`} className="estim-skel-row">
                    <td className="foot-label" colSpan={2}><span className="estim-skel-bar estim-skel-bar--lot" /></td>
                    {years.map((y) => <td key={y}><span className="estim-skel-bar" /></td>)}
                  </tr>
                ))
              ) : (<>
              <tr className="estimation-besoins-total">
                <td className="foot-label" colSpan={2}>Total financier</td>
                {years.map((y) => {
                  const fullIdx = years.indexOf(y);
                  const total = totalForYear(y);
                  const prev = fullIdx > 0 ? totalForYear(years[fullIdx - 1]) : 0;
                  return (
                    <td key={y} className={isEditable(y) ? '' : 'is-past'}>
                      {`${fmtEuro(total)}${fmtVariation(total, prev)}`}
                    </td>
                  );
                })}
              </tr>
              <tr className="estimation-besoins-foot-sub">
                <td className="foot-label" colSpan={2}>Nombre de commandes</td>
                {years.map((y) => (
                  <td key={y} className={isEditable(y) ? '' : 'is-past'}>{piedCompteur(y, 'nombre')}</td>
                ))}
              </tr>
              <tr className="estimation-besoins-foot-detail">
                <td className="foot-label" colSpan={2}>BC</td>
                {years.map((y) => (
                  <td key={y} className={isEditable(y) ? '' : 'is-past'}>{piedCompteur(y, 'bc')}</td>
                ))}
              </tr>
              <tr className="estimation-besoins-foot-detail">
                <td className="foot-label" colSpan={2}>MS</td>
                {years.map((y) => (
                  <td key={y} className={isEditable(y) ? '' : 'is-past'}>{piedCompteur(y, 'ms')}</td>
                ))}
              </tr>
              <tr className="estimation-besoins-foot-sub">
                <td className="foot-label" colSpan={2}>Montant moyen par commande</td>
                {years.map((y) => (
                  <td key={y} className={isEditable(y) ? '' : 'is-past'}>
                    {fmtEuro(isEditable(y) ? 0 : (piedAnnee(y)?.montant_moyen ?? 0))}
                  </td>
                ))}
              </tr>
              </>)}
            </tfoot>
          </table>
        </div>
        </div>
      </SectionPliable>
      {tip && (
        <div className="estimation-besoins-tip" style={{ left: tip.x, top: tip.y }}>{tip.text}</div>
      )}
      <SuggererLotModal isOpen={suggestOpen} onClose={() => { setSuggestOpen(false); refetchMaj(); }} />
    </>
  );
};

export default EstimationBesoins;
